import { ptBR } from 'date-fns/locale';
import { NZ_DATE_LOCALE, NZ_I18N, pt_BR } from 'ng-zorro-antd/i18n';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { provideEcharts } from 'ngx-echarts';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { catchError, Subject, takeUntil, timeout, TimeoutError } from 'rxjs';

import { DatePipe, DOCUMENT, registerLocaleData, TitleCasePipe } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import pt from '@angular/common/locales/pt';
import { APP_INITIALIZER, Component, ErrorHandler, importProvidersFrom, Inject, inject, isDevMode, LOCALE_ID } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavigationCancel, NavigationEnd, provideRouter, Router, RouterModule, TitleStrategy, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { GroupGeneralSettingsService } from '@domain/groups/services/group-general-settings/group-general-settings.service';
import { FamilyService } from '@domain/kids/services/family.service';
import { environment } from '@env/environment';
import { provideGoogleAnalytics, provideGoogleAnalyticsRouter } from '@hakimio/ngx-google-analytics';
import { DEFAULT_COLOR } from '@ministrary/shared/constants/default-color.constants';
import { eUserStatus } from '@ministrary/shared/enums/user-status.enum';
import { GlobalErrorHandler } from '@ministrary/shared/handlers/global-error.handler';
import { AuthService } from '@ministrary/shared/services/auth/auth.service';
import { ChurchService } from '@ministrary/shared/services/church/church.service';
import { CustomNomenclaturesService } from '@ministrary/shared/services/custom-nomenclatures/custom-nomenclatures.service';
import { MemberTypesService } from '@ministrary/shared/services/member-types/member-types.service';
import { PermissionService } from '@ministrary/shared/services/permission/permission.service';
import { AccessBlockService } from '@ministrary/shared/services/access-block/access-block.service';
import { DownloadStoragePipe } from '@ministrary/widget/pipes/download-avatar/download-avatar.pipe';
import { ShortNamePipe } from '@ministrary/widget/pipes/short-name/short-name.pipe';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { jwtInterceptor } from '@shared/interceptors/jwt.interceptor';
import { ThemeService } from '@shared/services/theme/theme.service';
import { PageTitleStrategy } from '@shared/strategies/page-title.strategy';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';

import { APP_ROUTES } from './app.routes';
import { PwaUpdateService } from './core/services/pwa-update/pwa-update.service';
import { TranslocoHttpLoader } from './transloco-loader';
import { ScheduleGeneralSettingsService } from '@domain/volunteer/services/schedule-general-settings/schedule-general-settings.service';
import { PwaUpdateBlockerComponent } from './core/components/pwa-update-blocker/pwa-update-blocker.component';
import { SubscriptionAccessBlockerComponent } from './core/components/subscription-access-blocker/subscription-access-blocker.component';
import { ChurchStatusUtils } from '@shared/utils/church-status.util';
import { eRoles } from '@ministrary/shared/enums/roles.enum';

declare global {
  interface Window {
    Apex: any;
  }
}

registerLocaleData(pt);

function loadTheme(themeService: ThemeService) {
  return () => themeService.loadTheme();
}

function initConfiguration(
  authService: AuthService,
  churchService: ChurchService,
  familyService: FamilyService,
  customNomenclaturesService: CustomNomenclaturesService,
  permissionService: PermissionService,
  accessBlockService: AccessBlockService,
  groupGeneralSettingsService: GroupGeneralSettingsService,
  scheduleGeneralSettingsService: ScheduleGeneralSettingsService,
  memberTypesService: MemberTypesService
) {
  return async () => {
    localStorage.removeItem('custom_nomenclatures');

    if (authService.isOffline) return;

    await authService.load();
    if (!authService.isAuthenticated) return;
    if (authService.currentUser.status === eUserStatus.pending) return;

    await churchService.load();
    if (!churchService.selectedChurch()) return;

    const selectedChurch = churchService.selectedChurch()!;
    const subscriptionStatus = ChurchStatusUtils.getSubscriptionStatus(selectedChurch);
    const isPastDue = subscriptionStatus.code === 'past_due';
    const allowAccessWithOverdue = !!selectedChurch.allow_access_with_overdue;

    if (isPastDue && !allowAccessWithOverdue) {
      const isSupport = authService.isSupport;
      const isSuperAdmin = !!authService.hasRole([eRoles.SUPER_ADMIN]);
      if (isSupport || isSuperAdmin) return;

      const isAdmin = !!authService.hasRole([eRoles.ADMIN]);
      accessBlockService.blockSubscriptionPastDue(isAdmin);
      return;
    }

    if (churchService.churchId) await permissionService.load();
    await memberTypesService.load();
    await familyService.load();

    await customNomenclaturesService.load();
    await groupGeneralSettingsService.load();
    await scheduleGeneralSettingsService.load();
  };
}

function initPwaUpdate(pwaUpdateService: PwaUpdateService) {
  return () => {
    void pwaUpdateService.load();
  };
}

function checkConnection(router: Router) {
  return () => {
    if (!window.navigator.onLine) {
      if (window.location.pathname !== '/offline') router.navigateByUrl('/offline');
    }

    window.addEventListener('offline', () => {
      if (window.location.pathname !== '/offline') router.navigateByUrl('/offline');
    });

    window.addEventListener('online', () => {
      if (window.location.pathname === '/offline') {
        window.location.reload();
      }
    });
  };
}

function checkInternetSpeed(http: HttpClient, notificationService: NzNotificationService) {
  return () =>
    http
      .get(environment.baseUrl + '/assets/logo/logo.svg', { responseType: 'text' })
      .pipe(
        timeout(2000),
        catchError(error => {
          if (error instanceof TimeoutError) {
            notificationService.warning('Internet lenta!', 'As informações podem demorar para carregar.', {
              nzPlacement: 'top',
              nzDuration: 10000
            });
          }

          return [];
        })
      )
      .subscribe();
}

function loadLanguage(transloco: TranslocoService) {
  const defaultLang = transloco.getDefaultLang();
  return () => transloco.load(defaultLang);
}

@Component({
  standalone: true,
  imports: [RouterModule, PwaUpdateBlockerComponent, SubscriptionAccessBlockerComponent],
  selector: 'mt-root',
  template: `
    @if (!accessBlockService.isBlocked()) {
      <router-outlet></router-outlet>
    }

    <mt-subscription-access-blocker />
    <mt-pwa-update-blocker />
  `
})
export class AppComponent {
  readonly accessBlockService = inject(AccessBlockService);
  private router = inject(Router);

  destroy$ = new Subject<void>();

  constructor(@Inject(DOCUMENT) private document: Document) {
    if (document.defaultView) document.defaultView.Apex.colors = [DEFAULT_COLOR, '#00E396', '#FEB019', '#FF4560', '#775DD0'];
  }

  ngAfterViewInit() {
    const loadingScreen = document.getElementById('loading-screen');
    if (!loadingScreen) return;

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
        loadingScreen.style.display = 'none';

        this.destroy$.next();
        this.destroy$.complete();
      }
    });
  }

  public static bootstrap() {
    bootstrapApplication(this, {
      providers: [
        { provide: APP_INITIALIZER, useFactory: checkInternetSpeed, multi: true, deps: [HttpClient, NzNotificationService] },
        { provide: APP_INITIALIZER, useFactory: checkConnection, multi: true, deps: [Router] },
        { provide: APP_INITIALIZER, useFactory: initPwaUpdate, multi: true, deps: [PwaUpdateService] },
        { provide: LOCALE_ID, useValue: 'pt-br' },
        { provide: NZ_DATE_LOCALE, useValue: ptBR },
        { provide: NZ_I18N, useValue: pt_BR },
        { provide: APP_INITIALIZER, useFactory: loadTheme, multi: true, deps: [ThemeService] },
        {
          provide: APP_INITIALIZER,
          useFactory: initConfiguration,
          multi: true,
          deps: [AuthService, ChurchService, FamilyService, CustomNomenclaturesService, PermissionService, AccessBlockService, GroupGeneralSettingsService, ScheduleGeneralSettingsService, MemberTypesService]
        },
        { provide: APP_INITIALIZER, useFactory: loadLanguage, multi: true, deps: [TranslocoService] },
        { provide: TitleStrategy, useClass: PageTitleStrategy },
        {
          provide: 'environment',
          useValue: environment
        },
        provideRouter(
          APP_ROUTES,
          withComponentInputBinding(),
          withInMemoryScrolling({
            anchorScrolling: 'enabled',
            scrollPositionRestoration: 'enabled'
          })
        ),
        importProvidersFrom(
          BrowserAnimationsModule,
          ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerImmediately'
          }),
          NzModalModule
        ),
        provideHttpClient(withInterceptors([jwtInterceptor])),
        provideTransloco({
          config: {
            availableLangs: ['pt-br'],
            defaultLang: 'pt-br',
            reRenderOnLangChange: true,
            prodMode: !isDevMode()
          },
          loader: TranslocoHttpLoader
        }),
        provideEnvironmentNgxMask(),
        provideEcharts(),
        provideGoogleAnalytics(environment.googleAnalyticsId),
        provideGoogleAnalyticsRouter(),
        NzNotificationService,
        TranslocoService,
        DownloadStoragePipe,
        { provide: ErrorHandler, useClass: GlobalErrorHandler, deps: [NzNotificationService, TranslocoService] },
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
        DatePipe,
        TitleCasePipe,
        ShortNamePipe
      ]
    }).catch(err => console.error(err));
  }
}
